import clsx from 'clsx';
import { Profiler, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Plate, PlateContent, useEditorValue } from 'platejs/react';
import styles from './PlateEditor.module.scss';
import { PlateEditorProps } from './types/types';
import { contentWrapClassNames } from './editorContentClassNames';
import { usePlateEditorLogic } from './hooks/usePlateEditorLogic';
import { PlateToolbar } from './toolbar/PlateToolbar/PlateToolbar';
import { FloatingToolbar } from './toolbar/FloatingToolbar/FloatingToolbar';
import { PlateErrorBoundary } from './components/PlateErrorBoundary';
import { PlateEditorConfigContext } from './PlateEditorConfigContext';
import { AIMenu, type AIMenuHandle } from './ai/AIMenu';
import { AI_TOGGLE_EVENT } from './toolbar/AIToolbarButton/AIToolbarButton';
import { FindReplacePanel, type FindReplacePanelHandle } from './findReplace/FindReplacePanel';
import { clearCellSelection } from './components/TableElements/cellSelection';
import { exportEditorToDocxFile } from './exporters/docxExport';
import { useToolbarHost } from './hooks/useToolbarHost';
import { useClipboardHandlers } from './hooks/useClipboardHandlers';
import { useMarkResetOnNavigation } from './hooks/useMarkResetOnNavigation';
import { useEditorKeyboardShortcuts } from './hooks/useEditorKeyboardShortcuts';
import { usePageBoundaries } from './hooks/usePageBoundaries';
import { PageBoundaryOverlay } from './components/PageBoundaryOverlay/PageBoundaryOverlay';
import { PageBreakSlotOverlay } from './components/PageBreakSlotOverlay/PageBreakSlotOverlay';
import { isPlatePerfEnabled } from './debug/platePerf';

// Re-export so the existing barrel + downstream consumers
// (`import { exportEditorToDocxFile } from '../PlateEditor'`) keep working
// after the helper moved to its own file. New code should prefer the
// direct import from `./exporters/docxExport`.
export { exportEditorToDocxFile };

/**
 * PlateValueWatcher — bridges Plate's internal value subscription to the
 * parent's `onChange` callback.
 *
 * Why this exists at all:
 *   Plate's `useEditorValue` hook only works inside a Plate provider (it
 *   reads from the jotai store the provider sets up). The parent of
 *   `<PlateEditor>` lives outside that provider, so it can't call the hook
 *   itself. PlateValueWatcher is a tiny child component rendered INSIDE
 *   the `<Plate>` provider; it can call the hook, and forwards every value
 *   change up to the parent via `onChange` + caches the latest value on
 *   `valueRef` (used by the error boundary for recovery).
 *
 * Why the `Promise.resolve().then(...)` deferral:
 *   Plate's value-change dispatch is synchronous — `useEditorValue` returns
 *   the new value during the same render cycle as the edit that produced
 *   it. Calling `onChange(value)` synchronously would let the parent
 *   schedule a setState during render, which React detects as a re-render
 *   loop and throws "Maximum update depth exceeded". Deferring the
 *   callback to a microtask breaks that synchronous chain — the parent's
 *   setState now lands in a new task, not nested inside Plate's render.
 *
 * Why a ref for `onChange` (instead of using it directly):
 *   Plate's value updates many times per typing burst. If we depended on
 *   `onChange` in the effect's deps array, every parent re-render that
 *   produced a new callback identity would re-fire the effect (and the
 *   deferred call) one extra time. Storing the latest callback in a ref
 *   means the effect only fires when `value` actually changes, while
 *   the deferred call still calls the *latest* parent callback.
 *
 * Renders nothing — pure side-effect bridge.
 */
function PlateValueWatcher({ onChange, valueRef }: { onChange?: (value: any) => void; valueRef?: React.MutableRefObject<any> }) {
    const value = (useEditorValue as any)();
    const ref = useRef(onChange);
    useEffect(() => {
        ref.current = onChange;
    }, [onChange]);
    useEffect(() => {
        if (valueRef) valueRef.current = value;
        // See the doc-comment above — defer to break Plate's synchronous
        // dispatch chain so the parent's setState doesn't nest inside
        // Plate's render and trigger "Maximum update depth exceeded".
        Promise.resolve().then(() => ref.current?.(value));
    }, [value, valueRef]);
    return null;
}

export function PlateEditor({
    className,
    placeholder,
    initialValue,
    readOnly = false,
    hideToolbar = false,
    fullHeight = false,
    onChange,
    toolbarVariant = 'full',
    toolbarHostId,
    onEditorMount,
    uploadSource = 'one-pager',
    enableAI = false,
    aiOrgId,
    aiExtraCompanyChips,
    mentionableUsers,
    compact = false,
    maxHeight,
    simplifyPaste = false,
    hiddenMarks,
    enableBlockDnd = false,
    renderedInBottomSheet = false,
    showPageGaps = false,
    reservedPageTopPx = 0,
    pnoLogoUrl,
    editorBandEnabled = true,
}: PlateEditorProps) {
    // The mention plugin is registered only when there are people to mention.
    // The live list still flows to MentionInputElement through
    // PlateEditorConfigContext (below), so the consumer can keep the same array
    // identity and update its contents without forcing the editor to rebuild.
    const enableMentions = Boolean(mentionableUsers?.length);
    const configValue = useMemo(
        () => ({ uploadSource, mentionableUsers: mentionableUsers ?? [], renderedInBottomSheet }),
        [uploadSource, mentionableUsers, renderedInBottomSheet],
    );
    const [editorKey, setEditorKey] = useState(0);
    const shellRef = useRef<HTMLDivElement>(null);
    // State-backed (not a plain useRef): `<Plate key={editorKey}>` remounts on
    // crash-recovery and swaps in a brand-new content-wrap DOM node. A stable
    // ref OBJECT would never notice that swap (its `.current` mutation isn't a
    // dependency change), so usePageBoundaries — which depends on this value —
    // would keep observing the now-detached old node. The callback ref below
    // re-fires `setContentWrapEl` on every attach, which IS a state change.
    const [contentWrapEl, setContentWrapEl] = useState<HTMLDivElement | null>(null);
    const editorValueRef = useRef<any>(initialValue ?? []);
    const aiMenuRef = useRef<AIMenuHandle>(null);
    const findReplacePanelRef = useRef<FindReplacePanelHandle>(null);

    const { editor } = usePlateEditorLogic({ initialValue, enableMentions, enableBlockDnd });

    const mountedRef = useRef<any>(null);
    useEffect(() => {
        if (mountedRef.current === editor) return;
        mountedRef.current = editor;
        onEditorMount?.(editor);
    });

    // Toolbar buttons (PlateToolbar + FloatingToolbar) dispatch this
    // custom event when clicked; we own the AIMenu ref, so we forward
    // to it here. Same gates as the ⌘J keyboard shortcut: enableAI on,
    // and not in read-only / history-preview mode. Listener target is
    // `document` because the toolbars portal outside the editor tree,
    // so a bubble-phase listener on the editor itself wouldn't catch
    // the dispatch.
    useEffect(() => {
        if (!enableAI || readOnly) return;
        const onToggle = () => {
            aiMenuRef.current?.toggle();
        };
        document.addEventListener(AI_TOGGLE_EVENT, onToggle);
        return () => document.removeEventListener(AI_TOGGLE_EVENT, onToggle);
    }, [enableAI, readOnly]);

    // Mark-reset chain — owns the 250 ms click debounce timer, the navigation
    // keyup handler, and `clearMarksAtCaret` (which the keyboard hook below
    // also calls on Enter). See useMarkResetOnNavigation.ts for the full
    // rationale (Google-Docs-style "new typing starts unformatted").
    const { clearMarksAtCaret, cancelPendingMarkReset, handleKeyUpReset, handleClickReset } = useMarkResetOnNavigation(editor);

    // Clipboard / drag-drop handlers. Owns `plainTextPasteRef` (⌘⇧V intent
    // flag) and exposes `markNextPasteAsPlain()` for the keyboard hook to
    // call when it detects ⌘⇧V — the paste event fires after keydown, so
    // we record intent here and consume it inside the hook's handlePaste.
    const { handlePaste, handleDrop, handleDragOver, handleCopy, handleCut, markNextPasteAsPlain } = useClipboardHandlers({
        editor,
        readOnly,
        uploadSource,
        simplifyPaste,
    });

    const focusEndOnPaddingClick = useCallback(
        (e: React.MouseEvent) => {
            // Cancel pending mark reset to avoid racing with dblclick word selection.
            cancelPendingMarkReset();
            if (readOnly) return;
            const target = e.target as HTMLElement;
            // Excel cell-selection: a mousedown that lands OUTSIDE any table cell
            // drops the single-cell highlight. A cell's own onMouseDown (which
            // bubbles to here AFTER running) sets the new selection when the
            // click IS in a cell, so we only clear when it isn't. Toolbar /
            // Find&Replace clicks are excluded so selecting a cell then clicking
            // a format button keeps the cell selected.
            if (!target.closest('td, th') && !target.closest('[role="toolbar"]') && !target.closest('[role="search"]')) {
                clearCellSelection(editor);
            }
            if (target.closest('[data-slate-editor="true"]')) return;
            if (target.closest('[role="toolbar"]')) return;
            // Find & Replace panel lives inside the shell; without this, its
            // mousedown is preventDefault'd and the input can't be focused/clicked.
            if (target.closest('[role="search"]')) return;
            e.preventDefault();
            try {
                const ed: any = editor;
                const end = ed?.api?.end?.([]);
                if (end) ed.tf.select(end);
                ed?.tf?.focus?.();
            } catch {
                /* editor not ready */
            }
        },
        [editor, readOnly, cancelPendingMarkReset],
    );

    // Keyboard shortcuts (⌘⇧V → plain paste, ⌘J → AI, ⌘F → find & replace,
    // Tab → indent/outdent in cells/lists, Enter → schedule mark reset).
    // Returns the single keydown-capture handler we attach to the shell.
    const { handleKeyDownCapture } = useEditorKeyboardShortcuts({
        editor,
        readOnly,
        enableAI,
        markNextPasteAsPlain,
        aiMenuRef,
        findReplacePanelRef,
        clearMarksAtCaret,
    });

    const onProfilerRender = useCallback((id: string, phase: string, actualDuration: number) => {
        if (isPlatePerfEnabled() && actualDuration > 8) {
            // eslint-disable-next-line no-console
            console.log('[plate-perf]', id, phase, `${actualDuration.toFixed(1)}ms`);
        }
    }, []);

    // Automatic page-boundary indicator (RECUR-27304). Gated on showPageGaps
    // only — independent of readOnly, so it still tracks the previewed
    // layout in history preview. `reservedPageTopPx` (RECUR-27604) keeps this
    // overlay's page-capacity math in lockstep with the SERVER export's own
    // logo-header reservation — see PlateEditorProps' own doc comment.
    // `editorBandEnabled` (RECUR-27604 editor-band flag) gates ONLY the
    // display-layer band preview inside the hook; it never touches
    // reservedPageTopPx's own capacity math.
    const { boundaries, bands, pageLeft, pageWidth } = usePageBoundaries(contentWrapEl, showPageGaps, reservedPageTopPx, editorBandEnabled);
    // RECUR-27604 §11.2: the ONLY remaining consumer of `editorBandEnabled`/
    // `reservedPageTopPx` here — gates the page-1 logo clearance (inside the
    // hook) and, below, the logo `<img>` itself. The slot/mask/edges are no
    // longer gated on this; they render on every `showPageGaps` document.
    const bandActive = editorBandEnabled && reservedPageTopPx > 0;

    const showToolbar = !readOnly && !hideToolbar;
    const toolbarHost = useToolbarHost(toolbarHostId);
    const toolbarEl = showToolbar ? <PlateToolbar variant={toolbarVariant} hiddenMarks={hiddenMarks} /> : null;

    const editorTree = (
        <Plate key={editorKey} editor={editor} readOnly={readOnly}>
            <PlateValueWatcher onChange={onChange} valueRef={editorValueRef} />
            {toolbarEl && toolbarHostId && toolbarHost ? createPortal(toolbarEl, toolbarHost) : null}
            {!readOnly && <FloatingToolbar variant={toolbarVariant} />}
            {enableAI && !readOnly && <AIMenu ref={aiMenuRef} aiOrgId={aiOrgId} extraCompanyChips={aiExtraCompanyChips} />}
            <div
                ref={shellRef}
                className={clsx(styles.shell, readOnly && styles.shellReadonly, fullHeight && styles.shellFullHeight, className)}
                data-testid="plate-editor-root"
                onMouseDown={focusEndOnPaddingClick}
                onKeyDownCapture={handleKeyDownCapture}
            >
                {!toolbarHostId && toolbarEl}
                {/* Find & Replace panel — positioned absolute inside the shell
                    so it floats over the editor content (shell has position: relative). */}
                {!readOnly && <FindReplacePanel ref={findReplacePanelRef} />}
                <div
                    ref={setContentWrapEl}
                    className={contentWrapClassNames({ fullHeight, bounded: maxHeight != null })}
                    style={maxHeight != null ? { maxHeight } : undefined}
                >
                    {/* Dev-only perf probe: logs slow (>8ms) PlateContent commits when
                        PLATE_PERF is enabled. React strips Profiler callbacks from
                        non-profiling production bundles, so this is free in prod. */}
                    <Profiler id="plate-content" onRender={onProfilerRender}>
                        <PlateContent
                            data-testid="plate-editor-content"
                            className={clsx(styles.content, compact && styles.contentCompact)}
                            placeholder={readOnly ? undefined : placeholder ?? ''}
                            readOnly={readOnly}
                            onPasteCapture={handlePaste}
                            onCopy={handleCopy}
                            onCut={handleCut}
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            onMouseUp={handleClickReset}
                            onKeyUp={handleKeyUpReset}
                        />
                    </Profiler>
                    {/* Automatic page-boundary indicator (RECUR-27304, "see the
                        pages"): a DOM SIBLING of <PlateContent> above, never a
                        Slate/Plate node, so import/save/load/PDF export stay
                        byte-unaffected. */}
                    {showPageGaps && <PageBoundaryOverlay boundaries={boundaries} />}
                    {/* Page-break slot (RECUR-27604 phase 4/§8; always-on full-width
                        break in §11, rev 6) — same DOM-sibling rules as
                        PageBoundaryOverlay above. Mount is `showPageGaps` ALONE now:
                        the slot/mask/edges must render on EVERY page-gap document,
                        logo or not (§11.1) — `boundaries`/`bands` are only ever
                        populated while showPageGaps is on, so without it there'd be
                        nothing for the overlay to paint anyway. `logoUrl` is the
                        ONLY remaining logo-gated piece: `bandActive` (editorBandEnabled
                        && reservedPageTopPx > 0) decides whether the real PNO URL or
                        `undefined` is passed, and the overlay renders an `<img>` iff
                        `logoUrl` is set. */}
                    {showPageGaps && (
                        <PageBreakSlotOverlay
                            logoUrl={bandActive ? pnoLogoUrl : undefined}
                            bands={bands}
                            pageLeft={pageLeft}
                            pageWidth={pageWidth}
                        />
                    )}
                </div>
            </div>
        </Plate>
    );

    return (
        <PlateEditorConfigContext.Provider value={configValue}>
            <PlateErrorBoundary lastValueRef={editorValueRef} onReset={() => setEditorKey((k) => k + 1)}>
                {/* Block drag-and-drop (DndPlugin + BlockDraggable) is opt-in via
                    `enableBlockDnd`. Only then do we mount the react-dnd
                    <DndProvider backend={HTML5Backend}> the drag hooks require.
                    With it OFF (default) we render the tree with no provider, so
                    two PlateEditor instances can co-mount without react-dnd's
                    "Cannot have two HTML5 backends at the same time" crash, and
                    the editor DOM matches the pre-feature shape (no BlockDraggable
                    wrapper to corrupt PDF export). HTML5Backend does not intercept
                    OS file drags, and the block drop hook is scoped to the
                    internal `block` type only (see BlockDraggable), so the native
                    image file-drop / paste handlers on <PlateContent> keep working
                    unchanged when DnD is enabled. */}
                {enableBlockDnd ? <DndProvider backend={HTML5Backend}>{editorTree}</DndProvider> : editorTree}
            </PlateErrorBoundary>
        </PlateEditorConfigContext.Provider>
    );
}
